import React, { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Accordion, Row } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronUp,
  faChevronDown,
  faSortAlt,
} from '@fortawesome/pro-solid-svg-icons'

import Button from 'design-system/button'
import {
  ButtonVariant,
  ButtonSizes,
  ButtonColors,
} from 'design-system/button/src/Button'
import { IFolderModel, IFolderSortSettingsField } from 'models/folder.model'
import * as fromActions from 'store/actions/folders.action'
import { IMainState } from 'store/states'
import { ICourseModel } from 'models/course.model'

import { Sort } from 'components/FolderSortConfiguration'
import { orderCourses } from 'helpers/courses/order'
import { advancedOrder } from 'enum/folder.enum'
import Edit from '../edit/Edit'
import Options from '../options/Options'
import { useFolderSort } from './hooks'
import { Courses } from './Courses'
import styles from './Folder.module.scss'
import Select from './Select'

interface IProps {
  item: IFolderModel
  create: boolean
}

const Folder: React.FC<IProps> = ({ item, create }) => {
  const [show, setShow] = useState(false)

  const dispatch = useDispatch()

  const { folders } = useSelector(({ general }: IMainState) => general.settings)

  const [open, setOpen] = useState(() => {
    const id = localStorage.getItem('openFolder')
    return item.id?.toString() === id
  })

  const [openAccordion, setOpenAccordion] = useState(() => {
    const id = localStorage.getItem('openFolder')
    if (item.id?.toString() === id) {
      return '0'
    }
    return '1'
  })

  const [edit, setEdit] = useState(false)
  const coursesData = item.courses?.data
  const sortSettings = item.sortSettings

  const handleOpenAccordion = () => {
    setOpen(!open)
    setOpenAccordion('0')
  }

  const handleChevron = (active: boolean) => {
    return active ? faChevronUp : faChevronDown
  }

  const onEditClick = () => setEdit(true)
  const onDeleteClick = (id: number) => {
    dispatch({ type: fromActions.DESTROY_FOLDER, payload: { id } })
  }

  const onSelectedCourseClickDefault = (courseId: string | number) => {
    dispatch({
      type: fromActions.ASSIGN_COURSE,
      payload: { idFolder: 1, idCourse: courseId },
    })
  }

  const { save, loading } = useFolderSort({
    item,
  })

  const onClose = () => {
    setShow(false)
  }

  const getList = useCallback(
    (
      array?: {
        id: string
        attributes: ICourseModel
      }[]
    ) =>
      orderCourses(array?.map(({ attributes }) => attributes) || [], {
        ...folders?.sort,
        ...sortSettings,
      }),
    [folders, sortSettings]
  )

  const [courses, setCourses] = useState(() => getList(coursesData))

  useEffect(() => {
    setCourses(getList(coursesData))
  }, [coursesData, getList])

  return edit ? (
    <Edit item={item} edit={setEdit} />
  ) : (
    <Accordion
      className={create ? styles.accordionDeactivate : styles.accordion}
      defaultActiveKey={openAccordion}
    >
      <div className="border-bottom d-flex justify-content-between align-items-center p-2 pl-3">
        <Accordion.Toggle
          as={Row}
          variant="link"
          eventKey="0"
          className="align-items-center w-100 pl-3"
          onClick={handleOpenAccordion}
        >
          <FontAwesomeIcon
            size="1x"
            color="#808080"
            icon={handleChevron(open)}
            className="mr-3"
          />
          {item.name}
        </Accordion.Toggle>
        <Options
          item={item}
          onDeleteClick={onDeleteClick}
          onEditClick={onEditClick}
          disable={false}
        />
      </div>
      <Accordion.Collapse eventKey="0">
        <>
          <div className={styles.itemFilter}>
            <div className="w-100 py-4">
              <div className="row no-gutters">
                <div className="col">
                  <Select item={item} create={create} />
                </div>
              </div>
            </div>
          </div>
          <Courses
            item={item}
            onSelectedCourseClickDefault={onSelectedCourseClickDefault}
            onOrderChange={async (courses) => {
              setCourses(courses)
              const fields = sortSettings?.fields
              if (fields)
                await save(
                  { fields },
                  courses.map((course) => `${course.id}`)
                )
            }}
            courses={courses}
          />
          <Sort
            show={show}
            customOrder={true}
            onClose={onClose}
            onSave={async (options, changed) => {
              if (!changed) {
                onClose()
                return
              }
              let fields: IFolderSortSettingsField[] | undefined
              let advancedOrderField: IFolderSortSettingsField | null = null
              for (const fieldOption of options) {
                fields ||= []
                const { option, direction } = fieldOption
                if (option) {
                  const field = { attribute: option.value, direction }
                  fields.push(field)
                  if (option.value === advancedOrder) advancedOrderField = field
                }
              }
              let newCourses: ICourseModel[] = []
              if (advancedOrderField) {
                const oldCustomOrder = item.sortSettings?.fields?.find(
                  (field) => field.attribute === advancedOrder
                )

                const directionChanged =
                  oldCustomOrder?.direction !== advancedOrderField.direction

                newCourses = orderCourses(courses || [], {
                  fields: directionChanged
                    ? fields
                    : fields?.filter(
                        ({ attribute }) => attribute !== advancedOrder
                      ),
                })
                advancedOrderField.direction = null
              }
              await save(
                { fields },
                newCourses.map((course) => `${course.id}`)
              )
              onClose()
            }}
            loading={loading}
            sortSettings={item.sortSettings}
          />
        </>
      </Accordion.Collapse>
    </Accordion>
  )
}

export default Folder
